import { useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import PageTemplate from '../../components/PageTemplate';
import AppContext from '../../services/context';
import { useError } from '../../hooks/useError.js';
import { useToast } from '../../hooks/useToast.js';
import {
  FEEDBACK_QUEUE,
  INVALIDATE_FEEDBACK,
  ANSWER_QUESTION,
} from '../../graphql/feedback';

const TYPES = ['', 'ERROR', 'IMPROVEMENT', 'CONTENT', 'QUESTION'];
const STATUSES = ['', 'PENDING', 'APPROVED', 'INVALIDATED', 'ANSWERED'];

const renderSuggestionItems = (label, items = []) => {
  if (!items.length) return null;

  return (
    <div>
      <p className="font-medium">{label}</p>
      <ul className="list-disc ml-4">
        {items.map((item) => (
          <li key={`${label}-${item.id || item.label}`}>
            {item.label}
            {item.id ? ` (#${item.id})` : ' (novo)'}
          </li>
        ))}
      </ul>
    </div>
  );
};

const RelationshipSuggestion = ({ feedback }) => {
  const meta = feedback.meta || {};
  if (meta.contentSubtype !== 'SPECIE_RELATIONSHIP_SUGGESTION') return null;

  return (
    <div className="text-xs bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-2 space-y-2">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span><strong>Tipo:</strong> sugestão de vínculo curado</span>
        <span><strong>Espécie:</strong> #{feedback.specieId}</span>
        <span><strong>Curadoria:</strong> {meta.reviewState || 'pending_curation'}</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {renderSuggestionItems('Efeitos', meta.therapeuticEffects)}
        {renderSuggestionItems('Categorias', meta.categories)}
        {renderSuggestionItems('Metabólitos', meta.metabolites)}
      </div>
      {meta.evidenceText && (
        <div>
          <p className="font-medium">Evidência em texto</p>
          <p className="whitespace-pre-wrap">{meta.evidenceText}</p>
        </div>
      )}
    </div>
  );
};

RelationshipSuggestion.propTypes = {
  feedback: PropTypes.shape({
    specieId: PropTypes.number,
    meta: PropTypes.shape({
      contentSubtype: PropTypes.string,
      reviewState: PropTypes.string,
      evidenceText: PropTypes.string,
      therapeuticEffects: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      })),
      categories: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      })),
      metabolites: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      })),
    }),
  }).isRequired,
};

const FeedbackQueueAdmin = () => {
  const { userState } = useContext(AppContext);
  const { showError } = useError();
  const toast = useToast();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [answeringId, setAnsweringId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [invalidatingId, setInvalidatingId] = useState(null);
  const [invalidationReason, setInvalidationReason] = useState('');

  const { data, loading, refetch } = useQuery(FEEDBACK_QUEUE, {
    variables: { type: type || null, status: status || null, limit: 200 },
    skip: !userState.isAdmin,
    onError: (error) => showError(error, { fallback: 'Falha ao carregar feedbacks.' }),
  });

  const [invalidate, { loading: invalidating }] = useMutation(INVALIDATE_FEEDBACK);
  const [answer, { loading: answering }] = useMutation(ANSWER_QUESTION);

  if (!userState.isAdmin) {
    return (
      <PageTemplate showSearchInput={false} alwaysShowLogo>
        <p className="text-center text-sm py-12">Acesso restrito a administradores.</p>
      </PageTemplate>
    );
  }

  const handleInvalidate = async (id) => {
    try {
      await invalidate({ variables: { id, reason: invalidationReason || null } });
      setInvalidatingId(null);
      setInvalidationReason('');
      await refetch();
      toast.success('Feedback invalidado.');
    } catch (error) {
      showError(error, { fallback: 'Falha ao invalidar feedback.' });
    }
  };

  const handleRefresh = async () => {
    try {
      await refetch();
    } catch (error) {
      showError(error, { fallback: 'Falha ao atualizar feedbacks.' });
    }
  };

  const handleAnswer = async (id) => {
    if (!answerText.trim()) return;
    try {
      await answer({ variables: { id, answer: answerText.trim() } });
      setAnsweringId(null);
      setAnswerText('');
      await refetch();
      toast.success('Resposta enviada.');
    } catch (error) {
      showError(error, { fallback: 'Falha ao enviar resposta.' });
    }
  };

  return (
    <PageTemplate showSearchInput={false} alwaysShowLogo>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <h1 className="text-2xl font-bold">Moderação de feedbacks</h1>

        <div className="flex gap-2 flex-wrap text-sm">
          <select className="rounded-lg border px-2 py-1 bg-transparent" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => <option key={t} value={t}>{t || 'Todos os tipos'}</option>)}
          </select>
          <select className="rounded-lg border px-2 py-1 bg-transparent" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s || 'Todos os status'}</option>)}
          </select>
          <button type="button" className="px-3 py-1 rounded-lg border" onClick={handleRefresh}>Atualizar</button>
        </div>

        {loading && <p className="text-sm">Carregando…</p>}

        <ul className="space-y-3">
          {(data?.feedbackQueue || []).map((f) => (
            <li key={f.id} className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span>#{f.id} · {f.type} · {f.status}</span>
                <span className="text-gray-500">
                  {f.user?.fullName || f.user?.email} · {f.user?.phone || ''}
                </span>
              </div>
              <p className="font-semibold text-sm">{f.title}</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{f.body}</p>
              <RelationshipSuggestion feedback={f} />
              {f.evidences?.length > 0 && (
                <ul className="text-xs text-gray-500 list-disc ml-4">
                  {f.evidences.map((e) => (
                    <li key={e.id}>
                      {e.kind === 'link'
                        ? `${e.linkType}: ${e.linkValue}`
                        : `${e.mimeType} · ${e.storageKey}`}
                    </li>
                  ))}
                </ul>
              )}
              {f.answer && (
                <div className="text-xs bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-2">
                  <strong>Resposta:</strong> {f.answer}
                </div>
              )}

              <div className="flex gap-2 pt-2 flex-wrap">
                {f.status !== 'INVALIDATED' && (
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() => { setInvalidatingId(f.id); setInvalidationReason(''); }}
                  >
                    Invalidar
                  </button>
                )}
                {f.type === 'QUESTION' && f.status !== 'ANSWERED' && (
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                    onClick={() => { setAnsweringId(f.id); setAnswerText(''); }}
                  >
                    Responder
                  </button>
                )}
              </div>

              {invalidatingId === f.id && (
                <div className="mt-2 space-y-2">
                  <textarea
                    className="w-full rounded-lg border px-2 py-1 text-xs bg-transparent"
                    placeholder="Motivo da invalidação (opcional)"
                    value={invalidationReason}
                    onChange={(e) => setInvalidationReason(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="text-xs px-2 py-1 rounded-lg border" onClick={() => setInvalidatingId(null)}>Cancelar</button>
                    <button type="button" className="text-xs px-2 py-1 rounded-lg bg-red-600 text-white disabled:opacity-60" disabled={invalidating} onClick={() => handleInvalidate(f.id)}>
                      Confirmar invalidação
                    </button>
                  </div>
                </div>
              )}

              {answeringId === f.id && (
                <div className="mt-2 space-y-2">
                  <textarea
                    className="w-full rounded-lg border px-2 py-1 text-xs bg-transparent"
                    placeholder="Resposta para o usuário"
                    rows={3}
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="text-xs px-2 py-1 rounded-lg border" onClick={() => setAnsweringId(null)}>Cancelar</button>
                    <button type="button" className="text-xs px-2 py-1 rounded-lg bg-green-700 text-white disabled:opacity-60" disabled={answering} onClick={() => handleAnswer(f.id)}>
                      Enviar resposta
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </PageTemplate>
  );
};

export default FeedbackQueueAdmin;
